import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useBusinessStore } from '../store/useBusinessStore';
import { useAuthStore } from '../store/useAuthStore';
import { supabase } from '../supabaseClient';
import { isRunning } from '../utils/competitors';

const DEMO_BUSINESS_IDS = ['9001', '9002', '9003'];
const REFRESH_MS = 10000;

export const isMissingCompetitors = (error) =>
  ['PGRST202', '42883', '42P01', 'PGRST205'].includes(error?.code) || /competitor_reports|request_competitor_report|cancel_competitor_report/i.test(error?.message || '');

const explain = (error, fallback) => {
  if (error?.code === '42501') return 'No tienes permiso sobre este negocio (solo el dueño o un administrador).';
  if (error?.code === '22023' && error?.message) return error.message.charAt(0).toUpperCase() + error.message.slice(1) + (/[.)]$/.test(error.message) ? '' : '.');
  return fallback;
};

// Análisis de competencia: se piden por la base y n8n los hace con Google Maps (un par de minutos). Aquí se piden, se miran o se cancelan.
export const useCompetitors = () => {
  const { activeBusinessId } = useBusinessStore();
  const { userRole } = useAuthStore();
  const isReadOnlyDemo = DEMO_BUSINESS_IDS.includes(String(activeBusinessId)) && userRole !== 'master';
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!activeBusinessId || isReadOnlyDemo) { setLoading(false); return; }
    const { data, error } = await supabase.from('competitor_reports')
      .select('id, query, city, status, competitors, market, analysis, found_count, error, created_at, finished_at')
      .eq('business_id', String(activeBusinessId)).order('created_at', { ascending: false }).limit(10);
    if (error) {
      const gone = isMissingCompetitors(error);
      setMissing(gone);
      if (!gone) toast.error(explain(error, 'No se pudo leer el análisis de competencia.'));
      setReports([]);
    } else {
      setMissing(false);
      setReports(data || []);
    }
    setLoading(false);
  }, [activeBusinessId, isReadOnlyDemo]);

  useEffect(() => { load(); }, [load]);
  const running = reports.some((r) => isRunning(r.status));
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [running, load]);

  const guardDemo = () => {
    if (isReadOnlyDemo) { toast.error('Modo solo lectura: las cuentas demo no se pueden modificar.'); return true; }
    return false;
  };

  const request = async ({ query, city }) => {
    if (guardDemo()) return false;
    setBusy(true);
    const { error } = await supabase.rpc('request_competitor_report', { p_business: String(activeBusinessId), p_query: String(query).trim(), p_city: String(city).trim() });
    setBusy(false);
    if (error) {
      if (isMissingCompetitors(error)) setMissing(true);
      toast.error(isMissingCompetitors(error) ? 'Falta ejecutar la migración 20261021a_competitor_reports.sql en Supabase.' : explain(error, 'No se pudo pedir el análisis.'));
      return false;
    }
    toast.success('Análisis en marcha: estará listo en un par de minutos.');
    await load();
    return true;
  };

  const cancel = async (id) => {
    if (guardDemo()) return false;
    const { error } = await supabase.rpc('cancel_competitor_report', { p_id: id });
    if (error) { toast.error(explain(error, 'No se pudo cancelar el análisis.')); return false; }
    await load();
    return true;
  };

  return { reports, loading, missing, busy, isReadOnlyDemo, reload: load, request, cancel };
};
